import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

export const VARIANTS = {
    black: { shell: "#161926", led: "#00F0FF" },
    silver: { shell: "#E6EBF3", led: "#38BDF8" },
    violet: { shell: "#5B2BD6", led: "#C084FC" },
};

export default function Headphones({ variant = "black", spinKey = 0 }) {
    const root = useRef(null);
    const spin = useRef({ vel: 0 });
    const variantRef = useRef(variant);
    variantRef.current = variant;

    const mats = useMemo(
        () => ({
            shell: new THREE.MeshPhysicalMaterial({
                color: VARIANTS.black.shell,
                metalness: 0.85,
                roughness: 0.28,
                clearcoat: 0.7,
                clearcoatRoughness: 0.25,
                envMapIntensity: 1.35,
            }),
            pad: new THREE.MeshStandardMaterial({
                color: "#15171F",
                roughness: 0.92,
                metalness: 0.08,
            }),
            joint: new THREE.MeshStandardMaterial({
                color: "#232838",
                metalness: 0.9,
                roughness: 0.35,
            }),
            led: new THREE.MeshStandardMaterial({
                color: VARIANTS.black.led,
                emissive: VARIANTS.black.led,
                emissiveIntensity: 2.4,
                toneMapped: false,
            }),
            inner: new THREE.MeshStandardMaterial({
                color: "#0A0C12",
                roughness: 0.6,
                metalness: 0.2,
            }),
        }),
        [],
    );

    const targets = useMemo(() => {
        const map = {};
        Object.entries(VARIANTS).forEach(([k, v]) => {
            map[k] = { shell: new THREE.Color(v.shell), led: new THREE.Color(v.led) };
        });
        return map;
    }, []);

    useEffect(() => {
        if (spinKey > 0) spin.current.vel = 6.5;
    }, [spinKey]);

    useFrame((state, delta) => {
        const g = root.current;
        if (!g) return;
        const t = state.clock.elapsedTime;

        g.position.y = Math.sin(t * 0.85) * 0.075;
        g.rotation.z = Math.sin(t * 0.5) * 0.02;

        spin.current.vel *= Math.exp(-delta * 2.0);
        if (spin.current.vel > 0.001) g.rotation.y += spin.current.vel * delta;

        const T = targets[variantRef.current];
        const k = 1 - Math.exp(-delta * 5);
        mats.shell.color.lerp(T.shell, k);
        mats.led.color.lerp(T.led, k);
        mats.led.emissive.lerp(T.led, k);
    });

    const cup = (s) => (
        <group
            position={[s * 0.78, -0.64, 0]}
            rotation={[0, 0, s * -0.04]}
        >
            <mesh rotation={[0, 0, Math.PI / 2]} material={mats.shell}>
                <cylinderGeometry args={[0.5, 0.5, 0.3, 48]} />
            </mesh>
            <mesh
                rotation={[0, Math.PI / 2, 0]}
                position={[s * 0.16, 0, 0]}
                material={mats.led}
            >
                <torusGeometry args={[0.33, 0.02, 16, 64]} />
            </mesh>
            <mesh
                rotation={[0, 0, Math.PI / 2]}
                position={[s * 0.165, 0, 0]}
                material={mats.inner}
            >
                <cylinderGeometry args={[0.29, 0.29, 0.02, 48]} />
            </mesh>
            <mesh
                rotation={[0, Math.PI / 2, 0]}
                position={[s * -0.15, 0, 0]}
                material={mats.pad}
            >
                <torusGeometry args={[0.32, 0.14, 20, 48]} />
            </mesh>
            <mesh
                rotation={[0, 0, Math.PI / 2]}
                position={[s * -0.12, 0, 0]}
                material={mats.inner}
            >
                <cylinderGeometry args={[0.26, 0.26, 0.03, 48]} />
            </mesh>
        </group>
    );

    const yoke = (s) => (
        <group position={[s * 1.0, 0, 0]}>
            <mesh material={mats.joint} position={[0, 0.08, 0]}>
                <sphereGeometry args={[0.09, 24, 24]} />
            </mesh>
            <mesh material={mats.joint} position={[0, -0.17, 0]}>
                <cylinderGeometry args={[0.042, 0.042, 0.5, 16]} />
            </mesh>
            <mesh material={mats.joint} position={[0, -0.42, 0]}>
                <sphereGeometry args={[0.07, 24, 24]} />
            </mesh>
        </group>
    );

    return (
        <group ref={root} scale={0.94} position={[0, 0.03, 0]}>
            <mesh material={mats.shell}>
                <torusGeometry args={[1.0, 0.07, 24, 80, Math.PI]} />
            </mesh>
            <mesh
                position={[0, 0.02, 0]}
                rotation={[0, 0, Math.PI * 0.05]}
                material={mats.pad}
            >
                <torusGeometry args={[0.88, 0.05, 20, 60, Math.PI * 0.9]} />
            </mesh>
            {[1, -1].map((s) => (
                <group key={s}>
                    {yoke(s)}
                    {cup(s)}
                </group>
            ))}
        </group>
    );
}
